import React, { useState } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import '../Customize_Css/Pagination.css';
import '../Customize_Css/ViewTickets.css';
import { Modal, Button, Dropdown, Table, Card, Form } from 'react-bootstrap';
import { FaEdit, FaFileExport, FaSearch, FaEye, FaTicketAlt } from 'react-icons/fa';
import Sidebar from '../Components/Siderbar';
import Footer from '../Components/Footer';
import { ToastContainer, toast } from 'react-toastify';
import useTickets from '../hooks/useTickets';
import * as XLSX from 'xlsx';
import { updateTicket } from '../api/tickets.api';

const LIST_COLUMNS = [
  { key: 'ticketNo', label: 'Ticket No' },
  { key: 'userId', label: 'User Id' },
  { key: 'issueType', label: 'Issue Type' },
  { key: 'orderNo', label: 'Order No' },
  { key: 'productName', label: 'Product Name' },
  { key: 'description', label: 'Description' },
  { key: 'contactEmailOrPhone', label: 'Contact Email Or Phone' },
];

const statusClass = (status) => {
  if (status === 'OPEN') return 'is-open';
  if (status === 'REOPENED') return 'is-reopened';
  if (status === 'CLOSED') return 'is-closed';
  return 'is-progress';
};

const ticketHistory = (ticket) => {
  if (!ticket) return [];
  return [
    ...(ticket.messages || []).map((msg) => ({
      type: 'message',
      content: msg.message,
      sender: msg.sender,
      time: msg.createdAt,
    })),
    ...(ticket.reopenHistory || []).map((reopen) => ({
      type: 'reopen',
      content: reopen.reason,
      reopenedBy: reopen.reopenedBy,
      time: reopen.reopenedAt,
    })),
  ].sort((a, b) => new Date(a.time) - new Date(b.time));
};

const ViewRaisedTickets = () => {
  const { tickets, setTickets, loading } = useTickets();
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [selectedItems, setSelectedItems] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [editStatus, setEditStatus] = useState('');
  const [editMessage, setEditMessage] = useState('');
  const [errors, setErrors] = useState({ status: '', message: '' });

  const filteredData = tickets.filter((t) =>
    Object.values(t).join(' ').toLowerCase().includes(searchTerm.toLowerCase())
  );

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentData = filteredData.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredData.length / itemsPerPage);

  const handleSelectAll = () => {
    setSelectedItems(
      selectedItems.length === currentData.length
        ? []
        : currentData.map((t) => t._id)
    );
  };

  const handleSelectItem = (_id) => {
    setSelectedItems((prev) =>
      prev.includes(_id) ? prev.filter((id) => id !== _id) : [...prev, _id]
    );
  };

  const handleExport = () => {
    const dataToExport = selectedItems.length
      ? tickets.filter((t) => selectedItems.includes(t._id))
      : tickets;

    const exportData = dataToExport.map((t) => ({
      ticketNo: t.ticketNo,
      userId: t.userId,
      issueType: t.issueType,
      orderNo: t.orderNo,
      productName: t.productName,
      description: t.description,
      toContactVia: t.toContactVia,
      contactEmailOrPhone: t.contactEmailOrPhone,
      status: t.status,
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Tickets');
    XLSX.writeFile(wb, 'tickets.xlsx');
  };

  const handleViewMore = (ticket) => {
    setSelectedTicket(ticket);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedTicket(null);
  };

  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setSelectedTicket(null);
  };

  const handleEdit = () => {
    if (selectedItems.length === 0) {
      return toast.error('Please select one ticket');
    }
    if (selectedItems.length > 1) {
      return toast.error('Please select only one ticket');
    }

    const ticket = tickets.find((t) => t._id === selectedItems[0]);
    setSelectedTicket(ticket);
    setEditStatus(ticket.status);
    setEditMessage('');
    setShowEditModal(true);
  };

  const handleUpdateTicket = async () => {
    let hasError = false;
    const newErrors = { status: '', message: '' };

    if (!editStatus) {
      newErrors.status = 'Please select a status';
      hasError = true;
    }
    if (!editMessage.trim()) {
      newErrors.message = 'Message is required';
      hasError = true;
    }
    setErrors(newErrors);
    if (hasError) return;

    try {
      await updateTicket(selectedTicket.ticketNo, {
        status: editStatus,
        message: editMessage.trim(),
        sender: 'ADMIN',
      });

      setTickets((prev) =>
        prev.map((t) =>
          t.ticketNo === selectedTicket.ticketNo ? { ...t, status: editStatus } : t
        )
      );

      setShowEditModal(false);
      setSelectedItems([]);
      setSelectedTicket(null);
      setEditMessage('');
      setEditStatus('');
      setErrors({ status: '', message: '' });
    } catch (err) {
      const serverMessage = err.response?.data?.error || err.response?.data?.message;
      setErrors({ status: '', message: serverMessage || 'Update failed. Try again.' });
    }
  };

  const totalPagesSafe = Math.max(1, totalPages);
  const rangeStart = filteredData.length === 0 ? 0 : indexOfFirstItem + 1;
  const rangeEnd = Math.min(indexOfLastItem, filteredData.length);
  const history = ticketHistory(selectedTicket);

  return (
    <>
      <Sidebar />
      <div className="tickets-page">
        <header className="tickets-hero">
          <div>
            <p className="tickets-kicker">Support</p>
            <h1>Raised Tickets</h1>
            <p>Review customer issues, update status, and export ticket records.</p>
          </div>
        </header>

        <section className="tickets-toolbar" aria-label="Ticket filters">
          <p className="tickets-toolbar-note">
            {selectedItems.length > 0
              ? <><strong>{selectedItems.length}</strong> selected. Edit one ticket, or export the selection.</>
              : 'Select one ticket to edit its status, or export the list.'}
          </p>
          <div className="tickets-toolbar-actions">
            <div className="tickets-search">
              <FaSearch aria-hidden="true" />
              <input
                type="text"
                className="form-control"
                placeholder="Search tickets..."
                aria-label="Search tickets"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
              />
            </div>
            <Dropdown>
              <Dropdown.Toggle className="tickets-action-btn">
                Actions
              </Dropdown.Toggle>
              <Dropdown.Menu>
                <Dropdown.Item onClick={handleEdit}>
                  <FaEdit className="me-2" /> Edit
                </Dropdown.Item>
                <Dropdown.Item onClick={handleExport}>
                  <FaFileExport className="me-2" /> Export
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          </div>
        </section>

        <Card className="tickets-card">
          <div className="tickets-card-head">
            <div>
              <h2>All Tickets</h2>
              <p>{searchTerm ? `Results for “${searchTerm}”` : 'Showing every raised ticket'}</p>
            </div>
            <span className="tickets-count">
              <FaTicketAlt aria-hidden="true" />
              {filteredData.length} {filteredData.length === 1 ? 'ticket' : 'tickets'}
            </span>
          </div>
          <Card.Body className="tickets-card-body">
            <Table responsive className="tickets-table">
              <thead>
                <tr>
                  <th className="tickets-check-cell tickets-pin-left">
                    <input
                      className="tickets-checkbox"
                      type="checkbox"
                      aria-label="Select all tickets on this page"
                      checked={selectedItems.length === currentData.length && currentData.length > 0}
                      onChange={handleSelectAll}
                    />
                  </th>
                  {LIST_COLUMNS.map((column) => (
                    <th key={column.key} className={column.key === 'ticketNo' ? 'tickets-pin-ticket' : undefined}>
                      {column.label}
                    </th>
                  ))}
                  <th className="tickets-pin-status">Status</th>
                  <th className="tickets-action-cell tickets-pin-action">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="10">
                      <div className="tickets-loading">
                        <div className="spinner-border text-success" role="status">
                          <span className="visually-hidden">Loading...</span>
                        </div>
                        <h3>Loading tickets</h3>
                        <p>Fetching the latest raised tickets.</p>
                      </div>
                    </td>
                  </tr>
                ) : currentData.length > 0 ? (
                  currentData.map((ticket) => (
                    <tr key={ticket._id} className={selectedItems.includes(ticket._id) ? 'is-selected' : ''}>
                      <td className="tickets-check-cell tickets-pin-left">
                        <input
                          className="tickets-checkbox"
                          type="checkbox"
                          aria-label={`Select ${ticket.ticketNo}`}
                          checked={selectedItems.includes(ticket._id)}
                          onChange={() => handleSelectItem(ticket._id)}
                        />
                      </td>
                      {LIST_COLUMNS.map((column) => (
                        <td key={column.key} className={column.key === 'ticketNo' ? 'tickets-pin-ticket' : undefined}>
                          <span
                            className={column.key === 'ticketNo' ? 'tickets-id' : 'tickets-ellipsis'}
                            title={ticket[column.key] || ''}
                          >
                            {ticket[column.key] || '-'}
                          </span>
                        </td>
                      ))}
                      <td className="tickets-pin-status">
                        <span className={`tickets-status ${statusClass(ticket.status)}`}>
                          {ticket.status || '-'}
                        </span>
                      </td>
                      <td className="tickets-action-cell tickets-pin-action">
                        <Button className="tickets-view-btn" onClick={() => handleViewMore(ticket)}>
                          <FaEye aria-hidden="true" /> View
                        </Button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="10">
                      <div className="tickets-empty">
                        <span><FaTicketAlt aria-hidden="true" /></span>
                        <h3>No tickets found</h3>
                        <p>
                          {searchTerm
                            ? 'Try a different ticket number, order, or product.'
                            : 'Raised tickets will appear here.'}
                        </p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </Card.Body>
          <div className="tickets-card-footer">
            <span className="tickets-page-summary">
              {filteredData.length > 0
                ? `Showing ${rangeStart}–${rangeEnd} of ${filteredData.length}`
                : 'Showing 0 tickets'}
            </span>
            <div className="tickets-pagination">
              <nav>
                <ul className="pagination mb-0" aria-label="Tickets pagination">
                  <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                    <button className="page-link btn-sm" onClick={() => setCurrentPage(1)} aria-label="First page">«</button>
                  </li>
                  <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                    <button className="page-link btn-sm" onClick={() => setCurrentPage(currentPage - 1)} aria-label="Previous page">&lt;</button>
                  </li>
                  <li className="page-item active">
                    <button className="page-link btn-sm" aria-current="page">{currentPage}</button>
                  </li>
                  <li className={`page-item ${currentPage === totalPagesSafe ? 'disabled' : ''}`}>
                    <button className="page-link btn-sm" onClick={() => setCurrentPage(currentPage + 1)} aria-label="Next page">&gt;</button>
                  </li>
                  <li className={`page-item ${currentPage === totalPagesSafe ? 'disabled' : ''}`}>
                    <button className="page-link btn-sm" onClick={() => setCurrentPage(totalPagesSafe)} aria-label="Last page">»</button>
                  </li>
                </ul>
              </nav>
            </div>
          </div>
        </Card>

        <div className="tickets-footer">
          <Footer />
        </div>

        <Modal show={showModal} onHide={handleCloseModal} centered size="lg" dialogClassName="ticket-details-modal">
          <Modal.Header closeButton>
            <Modal.Title>
              <span className="ticket-modal-icon"><FaTicketAlt aria-hidden="true" /></span>
              <span>
                <small>Support</small>
                Ticket Details
              </span>
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {selectedTicket && (
              <>
                <div className="ticket-details-summary">
                  <div>
                    <h3>{selectedTicket.ticketNo}</h3>
                    <p>{selectedTicket.issueType || 'Issue'} · {selectedTicket.orderNo || 'No order'}</p>
                  </div>
                  <span className={`tickets-status ${statusClass(selectedTicket.status)}`}>
                    {selectedTicket.status}
                  </span>
                </div>
                <div className="ticket-details-grid">
                  <div><span>User ID</span><strong>{selectedTicket.userId || '-'}</strong></div>
                  <div><span>Issue Type</span><strong>{selectedTicket.issueType || '-'}</strong></div>
                  <div><span>Order No</span><strong>{selectedTicket.orderNo || '-'}</strong></div>
                  <div><span>Product</span><strong>{selectedTicket.productName || '-'}</strong></div>
                  <div><span>Contact</span><strong>{selectedTicket.contactEmailOrPhone || '-'}</strong></div>
                  <div><span>Contact Via</span><strong>{selectedTicket.toContactVia || '-'}</strong></div>
                </div>
                <div className="ticket-description">
                  <span className="ticket-section-label">Description</span>
                  <p>{selectedTicket.description || '-'}</p>
                </div>
                <div className="ticket-history">
                  <h4>History</h4>
                  {history.length === 0 ? (
                    <div className="ticket-history-empty">No history available</div>
                  ) : (
                    <div className="ticket-history-list">
                      {history.map((item, index) => (
                        <div key={index} className={`ticket-history-item ${item.type === 'reopen' ? 'is-reopen' : ''}`}>
                          {item.type === 'message' ? (
                            <strong>Message ({item.sender})</strong>
                          ) : (
                            <strong>Reopened by {item.reopenedBy}</strong>
                          )}
                          <p>{item.content}</p>
                          <small>{new Date(item.time).toLocaleString()}</small>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}
          </Modal.Body>
          <Modal.Footer className="ticket-modal-footer">
            <Button className="tickets-modal-close" onClick={handleCloseModal}>Close</Button>
          </Modal.Footer>
        </Modal>

        <Modal show={showEditModal} onHide={handleCloseEditModal} centered dialogClassName="ticket-edit-modal">
          <Modal.Header closeButton>
            <Modal.Title>
              <span className="ticket-modal-icon"><FaEdit aria-hidden="true" /></span>
              <span>
                <small>Support</small>
                Edit Ticket
              </span>
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form className="ticket-edit-form">
              <Form.Group className="mb-3">
                <Form.Label>Status</Form.Label>
                <Form.Select
                  className={errors.status ? 'is-invalid' : ''}
                  value={editStatus}
                  onChange={(e) => {
                    setEditStatus(e.target.value);
                    if (e.target.value) setErrors((prev) => ({ ...prev, status: '' }));
                  }}
                >
                  <option value="">Select status</option>
                  <option value="IN_PROGRESS">IN_PROGRESS</option>
                  <option value="CLOSED">CLOSED</option>
                </Form.Select>
                {errors.status && <div className="invalid-feedback">{errors.status}</div>}
              </Form.Group>
              <Form.Group>
                <Form.Label>Message</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  className={errors.message ? 'is-invalid' : ''}
                  value={editMessage}
                  onChange={(e) => setEditMessage(e.target.value)}
                />
                {errors.message && <div className="invalid-feedback">{errors.message}</div>}
              </Form.Group>
            </Form>
          </Modal.Body>
          <Modal.Footer className="ticket-modal-footer">
            <Button className="tickets-modal-cancel" onClick={handleCloseEditModal}>Cancel</Button>
            <Button className="tickets-modal-save" onClick={handleUpdateTicket}>Update</Button>
          </Modal.Footer>
        </Modal>

        <ToastContainer
          position="top-center"
          autoClose={3000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          rtl={true}
          pauseOnFocusLoss
          draggable
          pauseOnHover
        />
      </div>
    </>
  );
};

export default ViewRaisedTickets;
